import React, { useRef, useEffect, useMemo } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './SplitText.css';

gsap.registerPlugin(ScrollTrigger);

const animationPresets = {
  fadeUp: {
    from: { opacity: 0, y: 30 },
    to: { opacity: 1, y: 0, ease: 'power3.out' },
  },
  wave: {
    from: { opacity: 0, y: 40, rotateX: -90 },
    to: { opacity: 1, y: 0, rotateX: 0, ease: 'back.out(1.7)' },
  },
  elastic: {
    from: { opacity: 0, scale: 0.3, rotateZ: -15 },
    to: { opacity: 1, scale: 1, rotateZ: 0, ease: 'elastic.out(1, 0.5)' },
  },
};

function SplitText({
  children,
  type = 'chars',
  animation = 'fadeUp',
  stagger = 0.03,
  duration = 0.6,
  delay = 0,
  once = true,
  className = '',
}) {
  const containerRef = useRef(null);
  const tweenRef = useRef(null);

  const textContent = typeof children === 'string' ? children : String(children || '');

  const elements = useMemo(() => {
    if (type === 'chars') {
      return textContent.split('').map((char, i) => (
        <span key={i} className="SplitText__char" style={{ display: 'inline-block' }}>
          {char === ' ' ? '\u00A0' : char}
        </span>
      ));
    }

    if (type === 'words') {
      return textContent.split(/(\s+)/).map((segment, i) => {
        if (/^\s+$/.test(segment)) {
          return <span key={i}>{segment}</span>;
        }
        return (
          <span key={i} className="SplitText__word" style={{ display: 'inline-block' }}>
            {segment}
          </span>
        );
      });
    }

    if (type === 'lines') {
      return textContent.split('\n').map((line, i) => (
        <span key={i} className="SplitText__line" style={{ display: 'block' }}>
          {line}
        </span>
      ));
    }

    return [<span key={0}>{textContent}</span>];
  }, [textContent, type]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const selectorMap = {
      chars: '.SplitText__char',
      words: '.SplitText__word',
      lines: '.SplitText__line',
    };

    const targets = el.querySelectorAll(selectorMap[type]);
    if (!targets.length) return;

    const preset = animationPresets[animation] || animationPresets.fadeUp;

    tweenRef.current = gsap.fromTo(targets, preset.from, {
      ...preset.to,
      duration,
      delay,
      stagger,
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
        toggleActions: once ? 'play none none none' : 'play reverse play reverse',
      },
    });

    return () => {
      if (tweenRef.current) {
        tweenRef.current.scrollTrigger?.kill();
        tweenRef.current.kill();
      }
    };
  }, [type, animation, stagger, duration, delay, once]);

  return (
    <span
      ref={containerRef}
      className={`SplitText ${className}`.trim()}
      aria-label={textContent}
    >
      {elements}
    </span>
  );
}

SplitText.defaultProps = {
  type: 'chars',
  animation: 'fadeUp',
  stagger: 0.03,
  duration: 0.6,
  delay: 0,
  once: true,
  className: '',
};

export default SplitText;
